<template>
    <v-dialog v-model="visible" persistent max-width="900px">
        <v-card light>
            <v-card-title class="justify-center">
                <v-spacer/>
                <span class="text-h5">
                    {{ form.id ? 'Edição' : 'Cadastro' }} de Novidade
                </span>
                <v-spacer/>
            </v-card-title>

            <v-card-text>
                <v-row>
                    <!-- Imagem de capa -->
                    <v-col cols="12" md="4">
                        <div class="caption grey--text text--darken-1 mb-1">Imagem de Capa</div>
                        <div
                            class="cover-upload"
                            :style="coverStyle"
                            @click="triggerCoverInput"
                            @dragover.prevent="coverDragging = true"
                            @dragleave.prevent="coverDragging = false"
                            @drop.prevent="onCoverDrop"
                            :class="{ 'drag-over': coverDragging }"
                        >
                            <div v-if="!coverPreview" class="cover-placeholder">
                                <v-icon large color="grey lighten-2">mdi-image-plus</v-icon>
                                <p class="caption grey--text mt-1 mb-0">Clique ou arraste uma imagem</p>
                            </div>
                            <v-progress-circular
                                v-if="coverUploading"
                                indeterminate
                                color="white"
                                class="cover-uploading"
                            />
                            <v-btn
                                v-if="coverPreview && !coverUploading"
                                x-small icon
                                color="grey darken-2"
                                class="cover-remove-btn"
                                @click.stop="removeCover"
                            >
                                <v-icon x-small>mdi-close</v-icon>
                            </v-btn>
                        </div>
                        <input
                            ref="coverInput"
                            type="file"
                            accept="image/*"
                            style="display: none;"
                            @change="onCoverSelected"
                        />
                    </v-col>

                    <!-- Título + Slug -->
                    <v-col cols="12" md="8">
                        <v-text-field
                            label="Título"
                            v-model="form.titulo"
                            dense
                            outlined
                            class="mb-3"
                            hide-details
                            @input="onTituloInput"
                        />

                        <v-text-field
                            label="Slug (URL amigável)"
                            v-model="form.slug"
                            dense
                            outlined
                            hide-details
                            prefix="/novidades/"
                            placeholder="minha-novidade"
                            :hint="'URL: /novidades/' + (form.slug || 'slug-gerado-automaticamente')"
                            persistent-hint
                        />
                    </v-col>
                </v-row>

                <v-row>
                    <v-col cols="12">
                        <div class="caption grey--text text--darken-1 mb-1">Descrição</div>
                        <RichTextEditor
                            v-model="form.descricao"
                            placeholder="Escreva o conteúdo da novidade. Use a barra de ferramentas para formatar, inserir imagens, GIFs e links."
                        />
                    </v-col>
                </v-row>
            </v-card-text>

            <v-card-actions>
                <v-btn text color="warning" @click="$emit('close')">Cancelar</v-btn>
                <v-spacer/>
                <v-btn color="success" @click="salvar" :loading="loading">
                    <v-icon>mdi-content-save</v-icon>&nbsp;Salvar
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
import RichTextEditor from '@/components/RichTextEditor';

export default {
    name: 'Novidade',

    components: { RichTextEditor },

    props: {
        visible: { type: Boolean, default: false },
        formData: { type: Object, default: Object },
    },

    data: () => ({
        form: {},
        loading: false,
        slugManuallyEdited: false,

        // Capa
        coverPreview:  null,
        coverUploading: false,
        coverDragging:  false,
    }),

    computed: {
        coverStyle() {
            return this.coverPreview
                ? { backgroundImage: `url(${this.coverPreview})` }
                : {};
        },
    },

    mounted() {
        this.form = this.formData;
        this.initCoverPreview();
    },

    watch: {
        formData(value) {
            this.form = value ? JSON.parse(JSON.stringify(value)) : {};
            this.slugManuallyEdited = !!this.form.slug;
            this.initCoverPreview();
        },
    },

    methods: {
        // ── Slug ─────────────────────────────────────────────────────────────
        onTituloInput() {
            if (!this.slugManuallyEdited) {
                this.form.slug = this.toSlug(this.form.titulo);
            }
        },

        toSlug(text) {
            if (!text) return '';
            return text
                .toLowerCase()
                .normalize('NFD').replace(/[\u0300-\u036f]/g, '') // remove acentos
                .replace(/[^a-z0-9\s-]/g, '')
                .trim()
                .replace(/\s+/g, '-')
                .replace(/-+/g, '-');
        },

        // ── Imagem de capa ───────────────────────────────────────────────────
        initCoverPreview() {
            if (this.form.imagem?.path) {
                this.coverPreview = `${this.$urlAPI}/novidades/files/${this.form.imagem.path}`;
            } else {
                this.coverPreview = null;
            }
        },

        triggerCoverInput() {
            this.$refs.coverInput.click();
        },

        onCoverDrop(e) {
            this.coverDragging = false;
            const file = e.dataTransfer.files?.[0];
            if (file) this.uploadCover(file);
        },

        onCoverSelected(e) {
            const file = e.target.files?.[0];
            if (file) this.uploadCover(file);
            e.target.value = '';
        },

        uploadCover(file) {
            // Preview local imediato
            const reader = new FileReader();
            reader.onload = (ev) => { this.coverPreview = ev.target.result; };
            reader.readAsDataURL(file);

            this.coverUploading = true;
            const fd = new FormData();
            fd.append('file', file);

            this.$http.post('files/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } })
                .then(resp => {
                    this.form.imagem = { path: resp.data.path, name: resp.data.name };
                })
                .catch(() => {
                    this.notify('Erro ao enviar imagem', 'error');
                    this.coverPreview = null;
                })
                .finally(() => { this.coverUploading = false; });
        },

        removeCover() {
            this.form.imagem = null;
            this.coverPreview = null;
        },

        // ── Salvar ───────────────────────────────────────────────────────────
        salvar() {
            const id = this.form.id;

            if (id) {
                if (!this.checkPermission('zapercontrol_novidades_atualizar')) {
                    alert('Usuário sem permissão');
                    return;
                }
                this.loading = true;
                this.$http.put(`novidades/${id}`, this.form)
                    .then(() => { this.$emit('onCadastro'); })
                    .catch(e => { alert(e); })
                    .finally(() => (this.loading = false));
                return;
            }

            if (!this.checkPermission('zapercontrol_novidades_adicionar')) {
                alert('Usuário sem permissão');
                return;
            }

            this.loading = true;
            this.$http.post('novidades', this.form)
                .then(() => { this.$emit('onCadastro'); })
                .catch(e => { alert(e); })
                .finally(() => (this.loading = false));
        },
    },
}
</script>

<style scoped>
.v-card {
    padding: 0 15px 0 15px;
}

.cover-upload {
    width: 100%;
    height: 180px;
    border: 2px dashed #ccc;
    border-radius: 8px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: #fafafa;
    background-size: cover;
    background-position: center;
    position: relative;
    transition: border-color 0.2s;
    overflow: hidden;
}

.cover-upload.drag-over {
    border-color: #1976d2;
    background-color: #e3f2fd;
}

.cover-placeholder {
    text-align: center;
    pointer-events: none;
}

.cover-uploading {
    position: absolute;
}

.cover-remove-btn {
    position: absolute;
    top: 6px;
    right: 6px;
    background: rgba(255,255,255,0.85) !important;
}
</style>
